import { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { MoreVertical, ArrowRightLeft, Undo2, Trash2, Check, X as XIcon, Package, AlertTriangle, Forklift, StickyNote } from 'lucide-react';
// The board's 3-dot menus use a menu that opens next to the dots at any page zoom (see MouseMenu.jsx).
import {
  MouseMenu as DropdownMenu, MouseMenuContent as DropdownMenuContent, MouseMenuItem as DropdownMenuItem,
  MouseMenuTrigger as DropdownMenuTrigger, MouseMenuSeparator as DropdownMenuSeparator,
} from './MouseMenu';
import { toast } from 'sonner';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { getRunColor } from './runColors';
import TransportEntryDetailModal from '../TransportEntryDetailModal';
import { getPageZoom } from '../../utils/desktopZoomFix';
import { isEntryComplete } from './entryStatus';
import api from '../../services/api';
import { API } from '../../App';

// Customer Management — the SAME window used everywhere else — opened from a Transport entry on the customer with the exact
// order open on the right (see openCustomerView below). Loaded only when first needed.
const ContactNotesTasksModal = lazy(() => import('../ContactNotesTasksModal').then((m) => ({ default: m.ContactNotesTasksModal })));

const STATUS_OPTIONS = [
  { value: 'complete', label: 'Mark Complete', icon: Check },
  { value: 'failed', label: 'Mark Failed', icon: XIcon },
  { value: 'collection', label: 'Mark Collection', icon: Package },
  { value: 'moffet', label: 'Mark Moffet', icon: Forklift },
];

// Encon brand orange.
const ENCON_ORANGE = '#ee7203';

// Live tracking colours (today only). En route = blue, on site = yellow, departed/complete = green.
// Soft, faded palette: the entry keeps its normal (white) background and only the progress is tinted; the outline is a
// faded version of the colour. The tabs are slightly softened solid colours.
const LIVE_BLUE = '#5b9cf6';
const LIVE_BLUE_BORDER = '#3b82f6';
const LIVE_BLUE_FILL = 'rgba(96,165,250,0.28)';
const LIVE_YELLOW = '#f2c744';
const LIVE_YELLOW_BORDER = '#eab308';
const LIVE_YELLOW_FILL = 'rgba(250,204,21,0.30)';
const LIVE_YELLOW_TEXT = '#422006';
const LIVE_GREEN_BORDER = '#16a34a';
const LIVE_GREEN_TAB = '#22a55a';
const LIVE_GREEN_FILL = 'rgba(74,222,128,0.30)';
const LIVE_RED_BORDER = '#ef4444';
const LIVE_RED_FILL = 'rgba(248,113,113,0.20)';
// Planned automatically and not yet confirmed by a person: a purple-to-pink outline, a pale pink background and a purple TBC.
const UNCONFIRMED_PURPLE = '#9333ea';
const UNCONFIRMED_BG = '#fdf2f8';
const UNCONFIRMED_BORDER_IMAGE = 'linear-gradient(135deg, #a855f7, #ec4899)';
const TAB_HEIGHT = 20;
// A little clear space between the TOP of a tab and the bottom of the entry above it.
const TAB_GAP = 8;

// "Show this entry" animation (global search > Show on Transport, and the Transport search bar): the entry zooms out
// (the whole entry, with its content, scaled evenly so nothing changes shape or spills over the box), holds for a few
// seconds with an orange outline and a pale orange shimmering background, then zooms back down — and the orange outline
// stays for a few more seconds before it fades away. Total length 8 seconds (the page clears the highlight just after).
const HIGHLIGHT_CSS = `
@keyframes c30-hl-zoom { 0% { scale: 1; } 6% { scale: 1.06; } 56% { scale: 1.06; } 64% { scale: 1; } 100% { scale: 1; } }
@keyframes c30-hl-shimmer { 0% { background-position: 0% 50%; } 100% { background-position: 200% 50%; } }
@keyframes c30-hl-bg-fade { 0%, 54% { opacity: 1; } 66%, 100% { opacity: 0; } }
@keyframes c30-hl-border { 0%, 86% { border-color: #ee7203; } 100% { border-color: rgba(238,114,3,0); } }
`;
const HIGHLIGHT_ZOOM_ANIMATION = 'c30-hl-zoom 8s ease-in-out forwards';
// VehicleCard separates entries with `space-y-1.5` (6px). The "next drop" tab is rendered under the on-site entry
// but must visually sit flush against the TOP of the entry below it, so it cancels that gap.
const ENTRY_GAP_PX = 6;

const formatMinutes = (minutes) => {
  const total = Math.max(0, Math.round(Number(minutes) || 0));
  if (total < 60) return `${total} mins`;
  const hours = Math.floor(total / 60);
  const rest = total % 60;
  return rest ? `${hours}h ${rest}m` : `${hours}h`;
};
const etaText = (eta) => (eta ? `ETA ${formatMinutes(eta.eta_minutes)} - ${eta.eta_time}` : '');

// UK postcodes always show with a space before the last three characters (B67AF -> B6 7AF, B664NT -> B66 4NT).
// Anything that is not a recognisable UK postcode is shown untouched.
const formatPostcode = (value) => {
  const text = String(value || '').trim();
  const compact = text.toUpperCase().replace(/\s+/g, '');
  if (/^[A-Z]{1,2}\d[A-Z\d]?\d[A-Z]{2}$/.test(compact)) return `${compact.slice(0, -3)} ${compact.slice(-3)}`;
  return text.toUpperCase();
};
// "Back to base" bar colours (brown while heading home, green while loading at base).
const BASE_BROWN_BORDER = 'rgba(139,94,60,0.65)';
const BASE_BROWN_FILL = 'rgba(110,231,160,0.50)'; // the bar keeps its brown outline and text; only its fill is a pale green
const BASE_BROWN_TEXT = '#5c3d24';
const BASE_BAR_HEIGHT = 26;

// Weights: two decimal places under 10 (0.85, 1.52); one decimal from 10 upwards (12.6).
const formatWeight = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '0.00';
  const hundredths = Math.round(n * 100) / 100; // so 9.999 (which rounds to 10) is treated as 10
  return hundredths >= 10 ? hundredths.toFixed(1) : hundredths.toFixed(2);
};

// Glass pill used for the customer name and the postcode: the same frosted white look as the buttons in the Global
// Header (light glass), with the run's colour as the outline. Both are clickable, so both have a hover state.
const GLASS_PILL_BG = 'linear-gradient(160deg, rgba(255,255,255,0.96) 0%, rgba(255,246,238,0.70) 55%, rgba(255,255,255,0.86) 100%)';
const GLASS_PILL_BG_HOVER = 'linear-gradient(160deg, #ffffff 0%, rgba(255,238,224,0.92) 55%, #ffffff 100%)';
const GLASS_PILL_SHADOW = 'inset 0 1.5px 0 rgba(255,255,255,0.95), inset 0 -1px 0 rgba(238,114,3,0.12), 0 2px 8px rgba(29,29,27,0.10), 0 1px 2px rgba(29,29,27,0.08)';
const GLASS_PILL_SHADOW_HOVER = 'inset 0 1.5px 0 #ffffff, inset 0 -1px 0 rgba(238,114,3,0.16), 0 4px 12px rgba(29,29,27,0.16), 0 1px 3px rgba(29,29,27,0.10)';
const ENCON_BLACK = '#1d1d1b';

const GlassPill = ({ as: Tag = 'span', color, className = '', style = {}, children, ...rest }) => {
  const [hover, setHover] = useState(false);
  return (
    <Tag
      {...rest}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className={`inline-block rounded-full font-bold cursor-pointer transition-shadow ${className}`}
      style={{
        background: hover ? GLASS_PILL_BG_HOVER : GLASS_PILL_BG,
        border: `1.5px solid ${color}`,
        boxShadow: hover ? GLASS_PILL_SHADOW_HOVER : GLASS_PILL_SHADOW,
        color: ENCON_BLACK,
        ...style,
      }}
    >
      {children}
    </Tag>
  );
};

/**
 * New Transport UI — an individual delivery rendered as its own liquid-glass
 * box (never a table row, never column-aligned fields). Passed into
 * `VehicleCard` via its `renderEntry` render-prop — reuses VehicleCard's
 * already-tested entry mutation helpers (`updateEntry`, `confirmDeleteEntry`,
 * `openEntryModalForEdit`, `unplanEntry`, `handleBulkMoveAcrossVehicles` —
 * see `ctx`) so there is zero duplicated business logic; only the visual
 * layer here is new.
 *
 * Layout: the LEFT block is line 1 = customer name, line 2 = [time] postcode (larger) then weight and
 * mileage (mileage in black). The RIGHT block lists the order numbers: the first three in full, one per
 * line, and any 4th to 6th as just their last 4 digits in a second column to the right of those.
 *
 * Live tracking (today only) replaces the old status pills: an entry being driven to gets a full-width
 * blue tab on top ("EN ROUTE - ETA ...") plus a blue outline and a pale blue background that fills from
 * left to right as the truck gets closer; an entry the truck is on site at gets a yellow outline and
 * background with a yellow "ON SITE" tab attached underneath it, and (next to it) a blue "ETA TO NEXT
 * DROP" tab attached to the top of the entry below; once the truck leaves, the entry is completed by the
 * geofence checker and shows the normal green completed look with no tabs.
 *
 * Click vs drag: dragging is dnd-kit `useSortable` (pointer-based — works the
 * same on mouse AND touch/iPad, unlike the old native HTML5 drag it replaced).
 * The drag handle is the card's main content area only (the 3-dot menu strip
 * stays isolated). Unlike native HTML5 drag, a completed pointer-drag still
 * fires a trailing native `click` on the same DOM node afterwards — `dragOccurredRef`
 * swallows exactly that one click so it doesn't also pop the entry open.
 */
const TransportOrderCard = ({ entry, runId, runNumber, ctx, onMoveSingle, liveStatus, eta, baseEta }) => {
  const {
    canEdit, isViewMode, isMultiMoveMode, isChecked, isHighlighted, onToggleCheck,
    updateEntry, queueVehicleSave, vehicle, openEntryModalForEdit,
    confirmDeleteEntry, unplanEntry, isEntryAutoPlanned, entryServiceIssues, currentDate, user,
    checkedEntries, checkedUnplannedItems = [], handleBulkMoveAcrossVehicles, openServiceIssue, dragSuppressRef, stopNumber,
  } = ctx;

  const cardRef = useRef(null);
  const dragOccurredRef = useRef(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [customerView, setCustomerView] = useState(null); // while Customer Management is open from this entry: { account, search, order, orderNumbers }
  const [openingCustomer, setOpeningCustomer] = useState(false);
  useEffect(() => {
    if (isHighlighted && cardRef.current) {
      cardRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [isHighlighted]);

  const draggable = canEdit && !isViewMode && !isMultiMoveMode;
  const runColor = getRunColor(runNumber);
  // "Show this entry" (global search / Transport search bar): see HIGHLIGHT_CSS above. The entry keeps its normal size and layout.
  const timeSlotColor = ENCON_ORANGE;
  // The postcode and the customer name sit on white glass pills outlined in the run's colour (see GlassPill above).
  const pillColor = runColor;
  const openOnMap = (e) => {
    e.stopPropagation();
    // Tells the Transport page to switch to Map View on this vehicle (listened for in TransportNewUI.jsx).
    window.dispatchEvent(new CustomEvent('c30-transport-open-map', { detail: { vehicleId: vehicle.id, entryId: entry.id } }));
  };
  const allOrderNumbers = [entry.order_number, ...(entry.order_numbers || [])].filter((n) => n && String(n).trim());
  // Right-hand order number list: the first three in full (one per line); a 4th, 5th and 6th as just their
  // last 4 digits in a second column to the right of those. Visual only — full numbers stay in
  // entry.order_numbers, so global search still matches. Anything beyond six shows as a small "+N".
  const mainOrderNumbers = allOrderNumbers.slice(0, 3).map((n) => `#${n}`);
  const extraOrderNumbers = allOrderNumbers.slice(3, 6).map((n) => `…${String(n).slice(-4)}`);
  const hiddenOrderCount = Math.max(0, allOrderNumbers.length - 6);
  // Auto-arranged (auto-planned) deliveries show a purple-to-pink outline, a pale pink background and a purple TBC (in the
  // gap where a completed / cancelled entry shows its tick / cross) in BOTH View and Edit mode until a person confirms
  // them (the green tick button, Edit mode only, below) — all of it goes the moment `entry.confirmed` is set.
  // Completed: a delivery marked complete, or a stock transfer the truck has visited and left (see entryStatus.js).
  const isComplete = isEntryComplete(entry);
  const hasLive = liveStatus === 'en_route' || liveStatus === 'at_drop';
  // The 3-dot strip down the right of the entry. In Edit mode it is the full menu (move, unplan, status, delete). In View mode it
  // is shown to anyone who may change a stop's status or add a note, with just those options.
  const canEditBoard = canEdit && !isViewMode;
  const hasEditRole = !!user && (user.role === 'super_admin' || ['manager', 'office'].includes(user.role) || user.can_edit_transport === true);
  const canMarkComplete = hasEditRole || user?.can_mark_complete === true;
  const canMarkFailed = hasEditRole || user?.can_mark_failed === true;
  const canAddNote = hasEditRole || user?.can_edit_notes === true;
  const canChangeStatus = canMarkComplete || canMarkFailed || canAddNote;
  const isUnconfirmed = isEntryAutoPlanned(entry) && !entry.confirmed;
  const needsConfirmBorder = isUnconfirmed && !hasLive;

  const nextDropEta = liveStatus === 'at_drop' ? (eta?.next_drop || null) : null;
  const showTopTab = liveStatus === 'en_route';
  const showBottomTabs = liveStatus === 'at_drop';
  // The truck is on site at the stop before this one, so that stop's right-aligned "ETA to next drop" tab sits on top of THIS
  // entry: this entry's top-right corner is square so the tab and the entry meet cleanly with no gap at the corner.
  const tabAbove = !!eta?.tab_above && !showTopTab && !showBottomTabs;
  // Between runs: a slim bar of its own — brown "back to base" under the last completed delivery, green "loading" above
  // the first stop of the next run. Both fill from left to right.
  const baseProgressPct = Math.round(Math.min(1, Math.max(0, Number(baseEta?.progress) || 0)) * 100);
  const baseBarText = (() => {
    if (!baseEta) return '';
    if (liveStatus === 'loading') {
      return baseEta.delayed
        ? `DELAYED LOADING · ${formatMinutes(baseEta.elapsed_minutes)}`
        : `LOADING · ${formatMinutes(baseEta.remaining_minutes)} left`;
    }
    if (baseEta.phase === 'at_base_done') return 'BACK AT BASE';
    return `BACK TO BASE · ${etaText(baseEta)}`;
  })();
  const progressPct = Math.round(Math.min(1, Math.max(0, Number(eta?.progress) || 0)) * 100);
  // On site: the yellow fills left to right over the set off-load time; once that time is used up the stop is DELAYED.
  const isDelayed = liveStatus === 'at_drop' && !!eta?.delayed;
  const onSiteProgressPct = isDelayed ? 100 : Math.round(Math.min(1, Math.max(0, Number(eta?.offload_progress) || 0)) * 100);
  const offloadLeftText = eta && eta.offload_remaining_minutes != null ? formatMinutes(eta.offload_remaining_minutes) : null;

  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({
    id: `entry-${entry.id}`,
    data: { type: 'entry', entry, entryId: entry.id, vehicleId: vehicle.id, runId: runId || 'main' },
    disabled: !draggable,
  });
  // Set synchronously during render (not in a useEffect) so it's already true by the time the
  // trailing native `click` dispatches right after drop — an effect would run too late.
  if (isDragging) dragOccurredRef.current = true;
  // The sortable node is the WHOLE entry (tabs + box) so a live tab always moves together with its box while dragging.
  const sortableStyle = {
    // The page is scaled with CSS zoom on laptops (utils/desktopZoomFix.js) but dnd-kit measures in real screen pixels, so
    // the sliding distance is converted into the zoomed page's own pixels. Without this the cards only slide 80% of the
    // way and overlap while you drag. Nothing changes when there is no zoom (getPageZoom() is 1).
    transform: CSS.Transform.toString(transform ? { ...transform, x: transform.x / getPageZoom(), y: transform.y / getPageZoom() } : transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

// Opens Customer Management on this entry's customer with the exact order open on the right — as if the customer had been
  // searched, Orders clicked and the order picked. An entry with several order numbers gets a tab for each along the top of the
  // order (shown only when opened from Transport). Stock transfers (the customer is a branch, not a CRM account) and orders that
  // are no longer held as a full parsed order keep opening the entry details window instead, which can still show them.
  const openCustomerView = async () => {
    if (openingCustomer) return;
    if (entry.status === 'stock_transfer') { setShowViewModal(true); return; }
    const numbers = allOrderNumbers.map((n) => String(n).trim().replace(/^#/, '')).filter(Boolean);
    if (numbers.length === 0) { setShowViewModal(true); return; }
    setOpeningCustomer(true);
    try {
      const found = (await Promise.all(numbers.map((num) => api
        .get(`${API}/customer-pricing/find-order`, { params: { order_number: num } })
        .then((res) => (res.data?.found ? res.data.order : null))
        .catch(() => null)))).filter(Boolean);
      if (found.length === 0) { setShowViewModal(true); return; }
      const first = found[0];
      setCustomerView({
        account: first.account_id ? { id: first.account_id, name: first.customer_name || entry.customer_name || '' } : null,
        search: first.account_id ? null : (first.customer_name || entry.customer_name || ''),
        order: { orderId: first.id, orderNumber: first.order_number },
        orderNumbers: numbers,
      });
    } finally {
      setOpeningCustomer(false);
    }
  };

  // Clicking the customer's name does the same as clicking the entry box in View mode. In Edit mode nothing changes: the click
  // carries on to the box, which opens the edit form as it always has.
  const handleCustomerNameClick = (e) => {
    if (!isViewMode || isMultiMoveMode) return; // Edit mode (edit form) and multi-move (ticks the entry) stay exactly as they were
    e.stopPropagation();
    if (dragOccurredRef.current || dragSuppressRef?.current) { dragOccurredRef.current = false; return; }
    openCustomerView();
  };

  const handleStatusChange = async (val) => {
    // A stock transfer keeps its "stock_transfer" status when it is completed — its completed_at is what finishes it.
    const updatedVehicle = (val === 'complete' && entry.status === 'stock_transfer')
      ? await updateEntry(entry.id, 'completed_at', new Date().toISOString(), runId)
      : await updateEntry(entry.id, 'status', val, runId);
    queueVehicleSave(vehicle.id, updatedVehicle);
    toast.success(`Marked as ${STATUS_OPTIONS.find((s) => s.value === val)?.label.replace('Mark ', '')}`);
  };

  // View mode opens the EXACT SAME rich entry modal used by Classic Transport's View Mode
  // (`TransportEntryDetailModal` — extracted 1:1 from `ViewModeGrid.jsx`'s entry dialog):
  // entry details / photo / status / service issue on the left, parsed order sliding in on
  // the right. Opens for ANY entry regardless of whether it has an embedded order snapshot —
  // the modal itself falls back to a live order lookup, matching classic exactly.
  const handleBoxClick = () => {
    if (dragOccurredRef.current || dragSuppressRef?.current) { dragOccurredRef.current = false; return; }
    if (isMultiMoveMode) { onToggleCheck(); return; }
    if (isViewMode) { openCustomerView(); return; }
    openEntryModalForEdit(entry, runId);
  };

  // Live outline + background. Applied inline so it always wins over the utility classes; never applied while the
  // card is highlighted by global search or ticked in multi-move mode, which keep their own looks.
  let liveCardStyle = {};
  if (!isHighlighted && !isChecked && liveStatus === 'en_route') {
    liveCardStyle = {
      borderColor: LIVE_BLUE_BORDER,
      borderWidth: '2px',
      backgroundImage: `linear-gradient(to right, ${LIVE_BLUE_FILL} ${progressPct}%, transparent ${progressPct}%)`,
    };
  } else if (!isHighlighted && !isChecked && liveStatus === 'at_drop') {
    liveCardStyle = {
      borderColor: LIVE_YELLOW_BORDER,
      borderWidth: '2px',
      backgroundImage: `linear-gradient(to right, ${LIVE_YELLOW_FILL} ${onSiteProgressPct}%, transparent ${onSiteProgressPct}%)`,
    };
  }
  // A cancelled (failed) entry is an ordinary card with a faded red outline and a faded red tint over its normal background.
  const failedCardStyle = (!isHighlighted && !isChecked && entry.status === 'failed')
    ? { borderColor: LIVE_RED_BORDER, backgroundImage: `linear-gradient(${LIVE_RED_FILL}, ${LIVE_RED_FILL})` }
    : {};
  // A completed entry: a faded green tint over its normal background (between the original pale green and the stronger one).
  const completeCardStyle = (!isHighlighted && !isChecked && isComplete)
    ? { borderColor: LIVE_GREEN_BORDER, backgroundImage: `linear-gradient(${LIVE_GREEN_FILL}, ${LIVE_GREEN_FILL})` }
    : {};
  // Unconfirmed automatic plan: the purple-to-pink outline is the card's OWN border (not an inset overlay), so it sits on
  // the outside of the box exactly like the other colours, on a pale pink background.
  const unconfirmedCardStyle = (needsConfirmBorder && !isHighlighted && !isChecked)
    ? {
        border: '2px solid transparent',
        backgroundImage: `linear-gradient(${UNCONFIRMED_BG}, ${UNCONFIRMED_BG}), ${UNCONFIRMED_BORDER_IMAGE}`,
        backgroundOrigin: 'border-box',
        backgroundClip: 'padding-box, border-box',
      }
    : {};
  // Shown by search: an orange outline that stays for the whole animation then fades (see HIGHLIGHT_CSS); the pale orange
  // shimmer is a separate layer inside the card (below) so the entry's own colours are untouched once it has gone.
  const highlightCardStyle = isHighlighted
    ? { border: '3px solid #ee7203', animation: 'c30-hl-border 8s ease-in-out forwards' }
    : {};
  const cardStyle = {
    ...liveCardStyle,
    ...completeCardStyle,
    ...failedCardStyle,
    ...unconfirmedCardStyle,
    ...highlightCardStyle,
  };
  // A full-width tab on top: both top corners square. A tab hanging under the left of the entry: only the bottom-LEFT corner
  // is square (the tab joins it) — the bottom-right corner stays rounded. A right-aligned tab on top: only the top-right
  // corner is square, so there is no small gap between the bottom of the tab and the entry.
  const cornerClass = showTopTab
    ? 'rounded-t-none rounded-b-xl'
    : (showBottomTabs ? 'rounded-bl-none rounded-br-xl rounded-t-xl' : (tabAbove ? 'rounded-tr-none rounded-tl-xl rounded-b-xl' : 'rounded-xl'));
  const menuCornerClass = (showTopTab || tabAbove) ? 'rounded-tr-none rounded-br-xl' : 'rounded-r-xl';

  return (
    <div
      ref={(node) => { cardRef.current = node; setNodeRef(node); }}
      data-entry-wrapper={entry.id}
      style={{
        ...(isHighlighted ? { ...sortableStyle, position: 'relative', zIndex: 30, animation: HIGHLIGHT_ZOOM_ANIMATION } : sortableStyle),
        ...(showTopTab ? { paddingTop: TAB_GAP } : {}),
      }}
      className="flex flex-col"
    >
      {isHighlighted && <style>{HIGHLIGHT_CSS}</style>}
      {/* LOADING bar (green) — a slim bar of its own above the first stop of the next run while the truck is loading at
          base; fills over the same time as the off-load time, and reads DELAYED LOADING once that time is used up. */}
      {liveStatus === 'loading' && baseEta && (
        <div
          data-testid={`loading-bar-${entry.id}`}
          className="relative mb-1 flex items-center overflow-hidden rounded-xl bg-white/90 backdrop-blur-xl shadow-sm"
          style={{ height: BASE_BAR_HEIGHT, border: `2px solid ${LIVE_GREEN_BORDER}` }}
        >
          <div className="absolute inset-y-0 left-0" style={{ width: `${baseProgressPct}%`, backgroundColor: LIVE_GREEN_FILL }} />
          <span className="relative px-3 text-[11px] font-bold uppercase tracking-wide truncate" style={{ color: '#14532d' }}>{baseBarText}</span>
        </div>
      )}

      {/* Live tab ABOVE the entry — en route: full width, blue, with the ETA */}
      {showTopTab && (
        <div
          data-testid={`live-top-tab-${entry.id}`}
          className="flex items-center px-2.5 text-white font-extrabold uppercase tracking-wide rounded-t-xl whitespace-nowrap overflow-hidden"
          style={{ height: TAB_HEIGHT, fontSize: 10, backgroundColor: LIVE_BLUE, border: `2px solid ${LIVE_BLUE_BORDER}`, borderBottom: 'none' }}
          title={eta ? `Estimated based on ${eta.eta_minutes}-minute travel time` : 'En route'}
        >
          <span className="truncate">{`EN ROUTE${eta ? ` · ${etaText(eta)}` : ''}`}</span>
        </div>
      )}

    <div
      data-testid={`order-card-${entry.id}`}
      data-entry-id={entry.id}
      style={cardStyle}
      className={`relative flex items-stretch ${cornerClass} border transition-all shadow-sm bg-white/90 backdrop-blur-xl ${
        isHighlighted ? '' :
        isChecked ? 'border-green-500 ring-2 ring-green-400 bg-green-50/90' :
        isComplete ? 'border-green-600' :
        entry.status === 'failed' ? 'border-red-500' :
        hasLive ? '' :
        needsConfirmBorder ? '' :
        isMultiMoveMode ? 'border-blue-300 hover:border-blue-500' :
        'border-slate-300/80 hover:shadow-lg hover:-translate-y-0.5'
      } ${draggable ? 'active:cursor-grabbing' : ''}`}
    >
      {/* Pale orange shimmering layer for the "show this entry" animation — fades away when the entry zooms back down */}
      {isHighlighted && (
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            borderRadius: 'inherit',
            backgroundImage: 'linear-gradient(110deg, rgba(255,237,213,0.92) 0%, rgba(253,186,116,0.70) 35%, rgba(255,237,213,0.92) 70%, rgba(253,186,116,0.70) 100%)',
            backgroundSize: '200% 100%',
            animation: 'c30-hl-shimmer 1.6s linear infinite, c30-hl-bg-fade 8s ease-in-out forwards',
          }}
        />
      )}

      {/* Insert-here arrow — multi-move mode, unselected lines only (mirrors the classic board's
          per-line arrow: click moves ALL currently-selected entries to just above this line) */}
      {canEdit && !isViewMode && isMultiMoveMode && !isChecked && (
        <button
          data-testid={`order-card-move-arrow-${entry.id}`}
          onClick={(e) => {
            e.stopPropagation();
            if (checkedEntries.length > 0 || checkedUnplannedItems.length > 0) handleBulkMoveAcrossVehicles(vehicle.id, entry.id, runId || 'main');
            else toast.info('Select entries first');
          }}
          className={`absolute -left-2.5 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full flex items-center justify-center shadow-md z-10 ${
            (checkedEntries.length > 0 || checkedUnplannedItems.length > 0) ? 'bg-green-500 hover:bg-green-600' : 'bg-slate-400'
          }`}
          title={(checkedEntries.length > 0 || checkedUnplannedItems.length > 0) ? 'Move selected entries above this line' : 'Select entries first'}
        >
          <span className="text-white text-[11px] font-bold leading-none">→</span>
        </button>
      )}

      {/* Main content — also the drag handle (dnd-kit `listeners`/`attributes`); clicking
          without dragging opens the entry (view modal in View Mode, edit form in Edit Mode).
          The 3-dot menu is its own full-height strip on the right, isolated from this.
          Highlighted state doubles padding here (a REAL size increase, not a transform) so the
          box genuinely takes up more room on the page and every line inside grows with it. */}
      <div
        className={`relative flex-1 min-w-0 cursor-pointer px-2.5 py-1.5`}
        style={{ paddingBottom: stopNumber != null && mainOrderNumbers.length >= 3 ? 18 : undefined }}
        onClick={handleBoxClick}
        {...(draggable ? { ...attributes, ...listeners } : {})}
      >
{stopNumber != null && (
          <span
            data-testid={`order-card-stop-number-${entry.id}`}
            title={`Stop ${stopNumber} on this run`}
            className="absolute bottom-0 right-0 flex items-center justify-center text-white font-extrabold leading-none rounded-tl-lg"
            style={{
              minWidth: 20, height: 16, padding: '0 5px', fontSize: 10,
              backgroundColor: isComplete ? LIVE_GREEN_TAB : runColor,
              borderBottomRightRadius: (canEditBoard || canChangeStatus) && !isMultiMoveMode ? 0 : 10,
            }}
          >
            {stopNumber}
          </span>
        )}
        <div className="flex items-start justify-between gap-2">
          {/* LEFT — line 1: customer name; line 2: [time] postcode (larger), weight, mileage */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 min-w-0">
              {isMultiMoveMode && (
                <span className={`w-3.5 h-3.5 rounded flex-shrink-0 flex items-center justify-center border-2 ${isChecked ? 'bg-green-500 border-green-500' : 'border-slate-300'}`}>
                  {isChecked && <Check size={9} className="text-white" strokeWidth={3} />}
                </span>
              )}
              <GlassPill
                color={pillColor}
                className={`min-w-0 max-w-full truncate text-[13px] px-3 py-0.5`}
                title={entry.customer_name}
                data-testid={`order-card-customer-${entry.id}`}
                onClick={handleCustomerNameClick}
              >
                {(entry.customer_name || 'Unnamed customer').toUpperCase()}
              </GlassPill>
              {entryServiceIssues?.[entry.id] && (
                <span className="w-3.5 h-3.5 bg-yellow-500 rounded flex items-center justify-center flex-shrink-0" title="Service issue logged"><span className="text-white text-[8px] font-bold">S</span></span>
              )}
              {entry.status === 'collection' && <Package size={13} className="flex-shrink-0" style={{ color: '#D97706' }} />}
              {entry.status === 'moffet' && <Forklift size={13} className="text-amber-700 flex-shrink-0" title="Moffet delivery" />}
            </div>

            <div className="flex items-center gap-1.5 mt-0.5 min-w-0">
              {entry.time_slot && (
                <span className={`font-bold flex-shrink-0 text-[11px]`} style={{ color: timeSlotColor }}>{entry.time_slot}</span>
              )}
              <GlassPill
                as="button"
                type="button"
                color={pillColor}
                className={`flex-shrink-0 text-[14px] px-3 py-0.5`}
                onClick={openOnMap}
                title="Show this vehicle on the map"
                data-testid={`order-card-postcode-${entry.id}`}
              >
                {formatPostcode(entry.postcode) || '—'}
              </GlassPill>
              <span className={`font-semibold text-blue-600 flex-shrink-0 text-[11px]`}>{`${formatWeight(entry.weight)}T`}</span>
              <span className={`font-semibold text-black flex-shrink-0 text-[11px]`}>{entry.miles ? `${entry.miles}mi` : '—'}</span>
              {canEdit && !isViewMode && !isMultiMoveMode && isEntryAutoPlanned(entry) && !entry.confirmed && (
                <button
                  data-testid={`confirm-entry-btn-${entry.id}`}
                  onClick={async (e) => {
                    e.stopPropagation();
                    const updatedVehicle = await updateEntry(entry.id, 'confirmed', true, runId);
                    queueVehicleSave(vehicle.id, updatedVehicle);
                    toast.success('Delivery confirmed');
                  }}
                  className="h-5 w-5 flex-shrink-0 rounded-full bg-green-500 hover:bg-green-600 flex items-center justify-center transition-colors"
                  title="Confirm this delivery"
                >
                  <Check size={11} className="text-white" strokeWidth={3} />
                </button>
              )}
            </div>
          </div>

          {/* RESULT SLOT — in the gap between the weight/mileage and the order numbers: a purple TBC while an automatic plan
              is still unconfirmed; otherwise a green tick on a completed entry or a red cross on a cancelled one (no background) */}
          {(isUnconfirmed || isComplete || entry.status === 'failed') && (
            <div
              className="flex items-center justify-center self-center flex-shrink-0 px-0.5"
              data-testid={`order-card-result-icon-${entry.id}`}
            >
              {isUnconfirmed
                ? <span className="text-[15px] font-extrabold tracking-wider" style={{ color: UNCONFIRMED_PURPLE }} data-testid={`order-card-tbc-${entry.id}`} title="Planned automatically — not confirmed yet">TBC</span>
                : isComplete
                  ? <Check size={22} className="text-green-600" strokeWidth={3.5} />
                  : <XIcon size={22} className="text-red-600" strokeWidth={3.5} />}
            </div>
          )}

          {/* RIGHT — order numbers: first three in full, 4th to 6th as their last 4 digits in a second column */}
          <div className="flex items-start gap-1.5 flex-shrink-0" data-testid={`order-card-order-numbers-${entry.id}`}>
            <div className={`flex flex-col items-end leading-tight font-medium text-slate-600 text-[11px]`}>
              {mainOrderNumbers.length > 0
                ? mainOrderNumbers.map((n) => <span key={n}>{n}</span>)
                : <span>No order #</span>}
            </div>
            {(extraOrderNumbers.length > 0 || hiddenOrderCount > 0) && (
              <div className={`flex flex-col items-end leading-tight font-medium text-slate-400 text-[11px]`}>
                {extraOrderNumbers.map((n, i) => <span key={`${n}-${i}`}>{n}</span>)}
                {hiddenOrderCount > 0 && <span title={`${hiddenOrderCount} more order numbers on this entry`}>{`+${hiddenOrderCount}`}</span>}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 3-dot menu — its own full-height strip on the right edge of the box */}
      {canEdit && !isViewMode && !isMultiMoveMode && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              data-testid={`order-card-menu-btn-${entry.id}`}
              onClick={(e) => e.stopPropagation()}
              className={`flex-shrink-0 w-7 flex items-center justify-center border-l border-slate-200/70 hover:bg-slate-100 ${menuCornerClass} text-slate-500 transition-colors`}
            >
              <MoreVertical size={15} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-white" onClick={(e) => e.stopPropagation()}>
            <DropdownMenuItem data-testid={`order-card-move-${entry.id}`} onClick={() => onMoveSingle(entry, runId)}>
              <ArrowRightLeft size={13} className="mr-2" /> Move
            </DropdownMenuItem>
            <DropdownMenuItem data-testid={`order-card-unplan-${entry.id}`} onClick={() => unplanEntry(entry, runId)}>
              <Undo2 size={13} className="mr-2" /> Unplan
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            {STATUS_OPTIONS.map((s) => {
              const Icon = s.icon;
              return (
                <DropdownMenuItem key={s.value} data-testid={`order-card-status-${s.value}-${entry.id}`} onClick={() => handleStatusChange(s.value)}>
                  <Icon size={13} className="mr-2" /> {s.label}
                </DropdownMenuItem>
              );
            })}
            <DropdownMenuItem data-testid={`order-card-service-issue-${entry.id}`} onClick={() => openServiceIssue(entry, runId)}>
              <AlertTriangle size={13} className="mr-2" /> Service Issue{entryServiceIssues?.[entry.id] ? ' ✓' : ''}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem data-testid={`order-card-delete-${entry.id}`} onClick={() => confirmDeleteEntry(entry.id, runId)} className="text-red-600">
              <Trash2 size={13} className="mr-2" /> Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {/* 3-dot strip in View mode — change a stop's status or add a note without going into Edit mode */}
      {!canEditBoard && !isMultiMoveMode && canChangeStatus && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              data-testid={`order-card-view-menu-btn-${entry.id}`}
              onClick={(e) => e.stopPropagation()}
              className={`flex-shrink-0 w-7 flex items-center justify-center border-l border-slate-200/70 hover:bg-slate-100 ${menuCornerClass} text-slate-500 transition-colors`}
            >
              <MoreVertical size={15} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-white" onClick={(e) => e.stopPropagation()}>
            {canMarkComplete && (
              <DropdownMenuItem data-testid={`order-card-view-status-complete-${entry.id}`} onClick={() => handleStatusChange('complete')}>
                <Check size={13} className="mr-2" /> Mark Complete
              </DropdownMenuItem>
            )}
            {canMarkFailed && (
              <DropdownMenuItem data-testid={`order-card-view-status-failed-${entry.id}`} onClick={() => handleStatusChange('failed')}>
                <XIcon size={13} className="mr-2" /> Mark Failed
              </DropdownMenuItem>
            )}
            {hasEditRole && STATUS_OPTIONS.filter((s) => s.value === 'collection' || s.value === 'moffet').map((s) => {
              const Icon = s.icon;
              return (
                <DropdownMenuItem key={s.value} data-testid={`order-card-view-status-${s.value}-${entry.id}`} onClick={() => handleStatusChange(s.value)}>
                  <Icon size={13} className="mr-2" /> {s.label}
                </DropdownMenuItem>
              );
            })}
            {canAddNote && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid={`order-card-view-note-${entry.id}`} onClick={() => setShowViewModal(true)}>
                  <StickyNote size={13} className="mr-2" /> Add Note / Photo
                </DropdownMenuItem>
              </>
            )}
            <DropdownMenuItem data-testid={`order-card-view-service-issue-${entry.id}`} onClick={() => openServiceIssue(entry, runId)}>
              <AlertTriangle size={13} className="mr-2" /> Service Issue{entryServiceIssues?.[entry.id] ? ' ✓' : ''}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {customerView && (
        <div onClick={(e) => e.stopPropagation()}>
          <Suspense fallback={null}>
            <ContactNotesTasksModal
              isOpen
              onClose={() => setCustomerView(null)}
              initialAccount={customerView.account}
              initialSearch={customerView.search}
              initialOrder={customerView.order}
              transportOrderNumbers={customerView.orderNumbers}
            />
          </Suspense>
        </div>
      )}

      {showViewModal && (
        <div onClick={(e) => e.stopPropagation()}>
          <TransportEntryDetailModal
            isOpen={showViewModal}
            entry={entry}
            vehicle={vehicle}
            runId={runId}
            selectedDate={currentDate}
            user={user}
            onClose={() => setShowViewModal(false)}
          />
        </div>
      )}
    </div>

      {/* Live tabs BELOW the entry — on site: yellow tab attached to this entry; beside it (when the vehicle has a
          following drop with an estimate) a blue "ETA to next drop" tab attached to the TOP of the entry below. */}
      {liveStatus === 'at_drop' && (
        <div
          className="flex items-start gap-1"
          style={{ height: nextDropEta ? TAB_HEIGHT + TAB_GAP : TAB_HEIGHT, marginBottom: nextDropEta ? -ENTRY_GAP_PX : 0 }}
          data-testid={`live-bottom-tabs-${entry.id}`}
        >
          <div
            className="flex items-center px-2.5 font-extrabold uppercase tracking-wide rounded-b-xl whitespace-nowrap overflow-hidden flex-shrink-0 max-w-[75%]"
            style={{ height: TAB_HEIGHT, fontSize: 10, backgroundColor: LIVE_YELLOW, color: LIVE_YELLOW_TEXT, border: `2px solid ${LIVE_YELLOW_BORDER}`, borderTop: 'none' }}
            data-testid={`live-on-site-tab-${entry.id}`}
            title={eta ? `On site for ${eta.arrived_minutes_ago} minutes so far (off-load time allowed: ${eta.offload_minutes ?? '?'} minutes)` : 'On site'}
          >
            <span className="truncate">
              {isDelayed
                ? `DELAYED ON SITE · ${formatMinutes(eta.arrived_minutes_ago)}`
                : `ON SITE${offloadLeftText ? ` · ${offloadLeftText} left` : ''}`}
            </span>
          </div>
          {!nextDropEta && baseEta?.phase === 'last_stop' && (
            <div
              className="flex items-center justify-end px-2.5 text-white font-extrabold uppercase tracking-wide rounded-b-xl whitespace-nowrap overflow-hidden flex-1 min-w-0"
              style={{ height: TAB_HEIGHT, fontSize: 10, backgroundColor: LIVE_GREEN_TAB, border: `2px solid ${LIVE_GREEN_BORDER}`, borderTop: 'none' }}
              data-testid={`live-back-at-base-tab-${entry.id}`}
              title="Estimated arrival back at base once this last drop is finished: the off-load time still to run plus the road travel time home"
            >
              <span className="truncate">{`BACK AT BASE · ${etaText(baseEta)}`}</span>
            </div>
          )}

          {nextDropEta && (
            <div
              className="flex items-center justify-end px-2.5 text-white font-extrabold uppercase tracking-wide rounded-tl-xl whitespace-nowrap overflow-hidden flex-1 min-w-0 self-end"
              style={{ height: TAB_HEIGHT, fontSize: 10, backgroundColor: LIVE_BLUE, border: `2px solid ${LIVE_BLUE_BORDER}`, borderBottom: 'none' }}
              data-testid={`live-next-drop-tab-${entry.id}`}
              title="Estimated arrival at the next drop: the off-load time still to run plus the travel time"
            >
              <span className="truncate">
                {nextDropEta.delayed
                  ? `ETA ESTIMATED · ${formatMinutes(nextDropEta.travel_minutes)} travel`
                  : etaText(nextDropEta)}
              </span>
            </div>
          )}
        </div>
      )}

      {/* BACK TO BASE bar (brown) — a slim bar of its own under the last completed delivery of a run, filling as the truck
          gets closer to base. Once the truck is back at base with nothing left to do it reads BACK AT BASE, full. */}
      {liveStatus === 'to_base' && baseEta && (
        <div
          data-testid={`base-bar-${entry.id}`}
          className="relative mt-1 flex items-center overflow-hidden rounded-xl bg-white/90 backdrop-blur-xl shadow-sm"
          style={{ height: BASE_BAR_HEIGHT, border: `1.5px solid ${BASE_BROWN_BORDER}` }}
        >
          <div className="absolute inset-y-0 left-0" style={{ width: `${baseProgressPct}%`, backgroundColor: BASE_BROWN_FILL }} />
          <span className="relative px-3 text-[11px] font-bold uppercase tracking-wide truncate" style={{ color: BASE_BROWN_TEXT }}>{baseBarText}</span>
        </div>
      )}
    </div>
  );
};

export default TransportOrderCard;
